import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { consultingTranslations } from '../translations/consulting';
import { PageSection, SectionContent, SectionTitle, BlackButtonLink } from '../styles/SharedStyles';

const ConsultingContainer = styled.div`
  width: 100%;
  font-family: 'Montserrat', sans-serif;
`;

const Section = styled(PageSection)`
  &#services {
    background-color: #f9f9f9;
  }
  
  &#process {
    background-color: white;
  }
  
  &#experience {
    background-color: #f9f9f9;
  }
  
  &#rates {
    background-color: white;
  }
`;

const ServicesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const ServiceBar = styled.div`
  padding: 2rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  width: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ServiceTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #333;
`;

const ServiceText = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: #666;
  margin: 0;
  text-align: left;
`;

const ExperienceList = styled.div`
  margin-top: 1.5rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const ExperienceItem = styled.div`
  margin-bottom: 1rem;
  padding-left: 1rem;
  border-left: 2px solid #ddd;
`;

const ExperienceText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #666;
  margin: 0;
  text-align: left;

  strong {
    font-weight: 600;
    color: #333;
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 1.5rem;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const PricingCard = styled.div`
  padding: 2rem;
  background: #f9f9f9;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const PricingTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #333;
`;

const PricingContent = styled.div`
  flex: 1;
`;

const PricingPrice = styled.div`
  font-size: 0.9rem;
  font-weight: 400;
  font-style: italic;
  color: #888;
  text-align: right;
  margin-top: 1.5rem;
`;

const PricingText = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #666;
  margin: 0;
  text-align: left;
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: 3rem;
`;

const Consulting = () => {
  const { language } = useLanguage();
  const consultingT = consultingTranslations[language];
  
  const renderExperienceText = (text) => {
    const parts = text.split(':');
    if (parts.length < 2) {
      return text;
    }
    const title = parts.shift();
    const description = parts.join(':');
    return (
      <>
        <strong>{title}:</strong>
        {description}
      </>
    );
  };
  
  return (
    <ConsultingContainer>
      {/* Services Section */}
      <Section id="services">
        <SectionContent>
          <SectionTitle>{consultingT.title}</SectionTitle>
          <ServicesContainer>
            <ServiceBar>
              <ServiceTitle>{consultingT.service1Title}</ServiceTitle>
              <ServiceText>{consultingT.service1Text}</ServiceText>
            </ServiceBar>
            
            <ServiceBar>
              <ServiceTitle>{consultingT.service2Title}</ServiceTitle>
              <ServiceText>{consultingT.service2Text}</ServiceText>
            </ServiceBar>
            
            <ServiceBar>
              <ServiceTitle>{consultingT.service3Title}</ServiceTitle>
              <ServiceText>{consultingT.service3Text}</ServiceText>
            </ServiceBar>

            <ServiceBar>
              <ServiceTitle>{consultingT.service4Title}</ServiceTitle>
              <ServiceText>{consultingT.service4Text}</ServiceText>
            </ServiceBar>
          </ServicesContainer>
        </SectionContent>
      </Section>
      
      {/* Process Section (How We Work) */}
      <Section id="process">
        <SectionContent>
          <SectionTitle>{language === 'de' ? 'Wie Wir Arbeiten' : 'How We Work'}</SectionTitle>
          <ServiceText style={{ maxWidth: '800px', margin: '1.5rem auto 0', textAlign: 'center', fontSize: '1.1rem' }}>
            {language === 'de' 
              ? 'Wir begleiten Sie von der ersten Idee bis zur erfolgreichen Umsetzung. Wie Wir Arbeiten ist transparent, kollaborativ und auf Ihre individuellen Bedürfnisse zugeschnitten.'
              : 'We support you from the initial idea to successful implementation. Our approach is transparent, collaborative, and tailored to your individual needs.'}
          </ServiceText>
        </SectionContent>
      </Section>
      
      {/* Experience Section */}
      <Section id="experience">
        <SectionContent>
          <SectionTitle>{consultingT.experienceTitle}</SectionTitle>
          <ExperienceList>
            <ExperienceItem>
              <ExperienceText>{renderExperienceText(consultingT.experience1)}</ExperienceText>
            </ExperienceItem>
            
            <ExperienceItem>
              <ExperienceText>{renderExperienceText(consultingT.experience2)}</ExperienceText>
            </ExperienceItem>
            
            <ExperienceItem>
              <ExperienceText>{renderExperienceText(consultingT.experience3)}</ExperienceText>
            </ExperienceItem>
            
            <ExperienceItem>
              <ExperienceText>{renderExperienceText(consultingT.experience4)}</ExperienceText>
            </ExperienceItem>

            {consultingT.experience5 && (
              <ExperienceItem>
                <ExperienceText>{renderExperienceText(consultingT.experience5)}</ExperienceText>
              </ExperienceItem>
            )}

            {consultingT.experience6 && (
              <ExperienceItem>
                <ExperienceText>{renderExperienceText(consultingT.experience6)}</ExperienceText>
              </ExperienceItem>
            )}

            {consultingT.experience7 && (
              <ExperienceItem>
                <ExperienceText>{renderExperienceText(consultingT.experience7)}</ExperienceText>
              </ExperienceItem>
            )}
            
            {consultingT.experience8 && (
              <ExperienceItem>
                <ExperienceText>{renderExperienceText(consultingT.experience8)}</ExperienceText>
              </ExperienceItem>
            )}
            
            {consultingT.experience9 && (
              <ExperienceItem>
                <ExperienceText>{renderExperienceText(consultingT.experience9)}</ExperienceText>
              </ExperienceItem>
            )}
            
            {consultingT.experience10 && (
              <ExperienceItem>
                <ExperienceText>{renderExperienceText(consultingT.experience10)}</ExperienceText>
              </ExperienceItem>
            )}
            
            {consultingT.experience11 && (
              <ExperienceItem>
                <ExperienceText>{renderExperienceText(consultingT.experience11)}</ExperienceText>
              </ExperienceItem>
            )}
            
            {consultingT.experience12 && (
              <ExperienceItem>
                <ExperienceText>{renderExperienceText(consultingT.experience12)}</ExperienceText>
              </ExperienceItem>
            )}
          </ExperienceList>
        </SectionContent>
      </Section>
      
      {/* Rates Section – hidden for now, do not delete
      <Section id="rates">
        <SectionContent>
          <SectionTitle>{consultingT.ratesTitle}</SectionTitle>
          <ServiceText style={{ maxWidth: '800px', margin: '1.5rem auto 2rem', textAlign: 'center', fontSize: '1.1rem' }}>
            {consultingT.ratesIntro}
          </ServiceText>
          
          <PricingGrid>
            <PricingCard>
              <PricingContent>
                <PricingTitle>{consultingT.package1Title}</PricingTitle>
                <PricingText>{consultingT.package1Text}</PricingText>
              </PricingContent>
              <PricingPrice>{consultingT.package1Price}</PricingPrice>
            </PricingCard>
            
            <PricingCard>
              <PricingContent>
                <PricingTitle>{consultingT.package2Title}</PricingTitle>
                <PricingText>{consultingT.package2Text}</PricingText>
              </PricingContent>
              <PricingPrice>{consultingT.package2Price}</PricingPrice>
            </PricingCard>
            
            <PricingCard>
              <PricingContent>
                <PricingTitle>{consultingT.package3Title}</PricingTitle>
                <PricingText>{consultingT.package3Text}</PricingText>
              </PricingContent>
              <PricingPrice>{consultingT.package3Price}</PricingPrice>
            </PricingCard>
            
            <PricingCard>
              <PricingContent>
                <PricingTitle>{consultingT.hourlyTitle}</PricingTitle>
                <PricingText>{consultingT.hourlyText}</PricingText>
              </PricingContent>
              <PricingPrice>{consultingT.hourlyPrice}</PricingPrice>
            </PricingCard>
          </PricingGrid>
        </SectionContent>
      </Section>
      */}

      {/* CTA button – always visible */}
      <CTASection>
        <BlackButtonLink href="https://calendly.com/plix-ai-consulting/meeting" target="_blank" rel="noopener noreferrer">
          {language === 'de' ? 'JETZT TERMIN VEREINBAREN' : 'BOOK A MEETING'}
        </BlackButtonLink>
      </CTASection>
    </ConsultingContainer>
  );
};

export default Consulting;
